import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Venue } from '@/types';
import { VenueForm } from './VenueForm';
import { showSuccess, showError } from '@/lib/swal';
import { Plus, Building2, X } from 'lucide-react';

interface VenueSelectorProps {
  unitId: string;
  value: string;
  onChange: (venueId: string, venueName?: string) => void;
  required?: boolean;
}

export function VenueSelector({ unitId, value, onChange, required = true }: VenueSelectorProps) {
  const { currentUser } = useAuth();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchVenues();
  }, [unitId]);

  const fetchVenues = async () => {
    try {
      setLoading(true);
      console.log('VenueSelector - Fetching venues for unitId:', unitId);
      
      const venuesQuery = query(
        collection(db, 'units', unitId, 'venues'),
        where('deleted', '==', false)
      );
      const venuesSnapshot = await getDocs(venuesQuery);
      console.log('VenueSelector - Raw snapshot size:', venuesSnapshot.size);
      
      const venuesData = venuesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Venue[];

      // Filter for active venues only and sort by name
      const activeVenues = venuesData.filter(venue => venue.status === 'active');
      activeVenues.sort((a, b) => a.name.localeCompare(b.name));
      
      console.log('VenueSelector - All venues:', venuesData);
      console.log('VenueSelector - Active venues:', activeVenues);
      
      setVenues(activeVenues);
    } catch (error) {
      console.error('Error fetching venues:', error);
      showError('Failed to load venues');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVenue = async (venueData: Partial<Venue>) => {
    if (!currentUser) return;

    setSaving(true);
    try {
      // Remove undefined values - Firestore doesn't accept them
      const cleanedData = Object.fromEntries(
        Object.entries(venueData).filter(([_, value]) => value !== undefined)
      );

      const newVenue = {
        ...cleanedData,
        unitId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: currentUser.uid,
        deleted: false,
      };

      const docRef = await addDoc(collection(db, 'units', unitId, 'venues'), newVenue);
      showSuccess('Venue created successfully!');
      setShowModal(false);
      
      // Refresh venues list
      await fetchVenues();
      
      // Select the newly created venue
      onChange(docRef.id, venueData.name);
    } catch (error) {
      console.error('Error creating venue:', error);
      showError('Failed to create venue');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="space-y-2">
        <Label>
          Venue {required && <span className="text-destructive">*</span>}
        </Label>
        <div className="flex gap-2">
          <Select
            value={value}
            onValueChange={(selectedVenueId) => {
              const selectedVenue = venues.find(v => v.id === selectedVenueId);
              onChange(selectedVenueId, selectedVenue?.name);
            }}
          >
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select a venue..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__custom__">Custom Location (Enter Manually)</SelectItem>
              {venues.map((venue) => (
                <SelectItem key={venue.id} value={venue.id}>
                  {venue.name} - {venue.address.split(',')[0]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowModal(true)}
            title="Create new venue"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Venue
          </Button>
        </div>
      </div>

      {/* Modal for creating new venue */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                <h2 className="text-xl font-bold">Create New Venue</h2>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-muted-foreground hover:text-foreground"
                disabled={saving}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <VenueForm
                onSave={handleCreateVenue}
                onCancel={() => setShowModal(false)}
                saving={saving}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

